import React, { useEffect, useState } from "react";
import { apiGet, apiPost, formatDateTime, formatDateTimeSLT, formatNumber } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import LoadingState from "@/components/common/LoadingState";
import ErrorState from "@/components/common/ErrorState";
import KpiTile from "@/components/dashboard/KpiTile";
import SmtpStatusCard from "@/components/admin/SmtpStatusCard";
import CseImportCard from "@/components/admin/CseImportCard";
import TradingViewCard from "@/components/admin/TradingViewCard";
import LaunchChecklistCard from "@/components/admin/LaunchChecklistCard";
import ApiCostStatusCard from "@/components/admin/ApiCostStatusCard";
import DeepAnalysisStatusCard from "@/components/admin/DeepAnalysisStatusCard";
import EmailConfigCard from "@/components/admin/EmailConfigCard";
import SoftLaunchInviteCard from "@/components/admin/SoftLaunchInviteCard";
import RunNowCard from "@/components/admin/RunNowCard";
import ReportQueueCard from "@/components/admin/ReportQueueCard";
import DiskUsageCard from "@/components/admin/DiskUsageCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Info, Globe, CheckCircle2, XCircle, CircleDashed } from "lucide-react";
import {
  CircleDot,
  RefreshCcw,
  Calendar,
  Database,
  AlertTriangle,
  Wifi,
  Server,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const SOURCE_META = {
  cse: {
    label: "CSE Live",
    dot: "🟢",
    description: "Pulled live from cse.lk tradeSummary",
    badgeClass:
      "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/25",
  },
  yfinance: {
    label: "Yahoo Finance",
    dot: "🟡",
    description: "Fallback: Yahoo Finance .CM tickers",
    badgeClass:
      "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/25",
  },
  carry_forward: {
    label: "Carry Forward",
    dot: "⚪",
    description: "Stock did not trade — last close carried forward",
    badgeClass: "bg-muted text-muted-foreground border-border",
  },
  mock: {
    label: "Mock Data",
    dot: "🔵",
    description: "Generated mock data (last resort or toggle ON)",
    badgeClass:
      "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/25",
  },
  none: {
    label: "Unknown",
    dot: "⚫",
    description: "No source recorded",
    badgeClass: "bg-muted text-muted-foreground border-border",
  },
};

// Friendly labels + scheduling rationale for every cron job. The hover-tooltip
// surfaces *why* a particular time was chosen — most are anchored around the
// CSE market session (open 09:30, close 14:30 Asia/Colombo).
const JOB_META = {
  prices_refresh: {
    label: "Prices refresh (CSE → yfinance → carry-forward)",
    summary: "Mon–Fri 17:30 — three hours after market close",
    rationale:
      "CSE market closes at 14:30. We wait until 17:30 to ensure all trades are settled and final closing prices are published on cse.lk before we scrape.",
  },
  tv_technicals: {
    label: "TradingView technical signals",
    summary: "Mon–Fri 18:00 — runs after the price scrape",
    rationale:
      "Refreshes RSI, MACD, moving-average ratings and volume signals from TradingView 30 minutes after the price scrape so technicals always reflect the latest close.",
  },
  cse_reports_check: {
    label: "CSE report discovery",
    summary: "Daily 18:30 — pulls new annual / interim PDFs",
    rationale:
      "Companies typically file annual and interim reports with the CSE after the market closes. We check at 18:30 to catch same-day filings before the analysis run at 20:00.",
  },
  cse_reports_analyse: {
    label: "Claude analysis of new reports",
    summary: "Daily 20:00 — extracts EPS / revenue / margins",
    rationale:
      "Runs Claude on any reports that came in earlier today. 20:00 lets the discovery job (18:30) finish downloading large multi-MB PDFs before analysis fires.",
  },
  weekly_ai_refresh: {
    label: "Weekly AI re-score",
    summary: "Sun 20:00 — re-scores every tracked symbol",
    rationale:
      "Sunday evening reruns the full Claude scoring pipeline against the latest fundamentals + technicals + news so Monday morning starts with a fresh BUY/HOLD/SELL signal.",
  },
  sector_analysis_refresh: {
    label: "Layer 6 — Sector Context analysis",
    summary: "Sun 21:00 — Claude scores every CSE sector",
    rationale:
      "Once per week Claude evaluates each of the ~20 CSE sectors on cycle position, valuation vs history, macro tailwinds, and foreign-investor sentiment. Each stock's COMB score then picks up a 0-10 sector contribution. Cost ≈ $0.25–$1 per run. Fires at 21:00 — one hour after the weekly AI re-score so the new sector verdicts flow through to the next composite rescore.",
  },
  dividend_announcements_sync: {
    label: "Dividend announcements sync",
    summary: "Mon-Fri 18:30 — pull declared dividends from CSE",
    rationale:
      "After market close on every trading day we hit the CSE approvedAnnouncement API, drill into each CASH DIVIDEND announcement (getAnnouncementById), and upsert XD/payment dates + DPS into the dividends collection. This is what flips an Estimated row to a Declared row (green badge + real XD date) on the Dividends page. Free, idempotent, ~30 detail calls/day.",
  },
  tv_fundamentals: {
    label: "TradingView fundamentals refresh",
    summary: "Sun 08:00 — PE, EPS, ROE, dividend yield",
    rationale:
      "Weekly refresh of PE, EPS, ROE and dividend yield from TradingView. Sunday morning is chosen because the market is closed (no risk of mid-session writes) and the data is ready for Monday opening.",
  },
  fundamentals_refresh_legacy: {
    label: "yfinance fundamentals fallback",
    summary: "Sun 09:00 — only fills gaps left by TradingView",
    rationale:
      "Runs an hour after the TradingView pull and only writes to symbols where TradingView did not return data (illiquid tickers without TV coverage).",
  },
  tv_dividend_topup: {
    label: "TradingView dividend yield top-up",
    summary: "Sun 08:30 — runs after the weekly fundamentals pull",
    rationale:
      "Sweeps every CSE symbol for the latest dividend yield + DPS using TradingView's `dividends_yield_current` and `dps_common_stock_prim_issue_fy` columns. After the pull it auto-recomputes estimated DPS, sector medians and composite scores so dividend-heavy names re-rank immediately.",
  },
  news_refresh: {
    label: "Daily news ingestion (FT.lk RSS)",
    summary: "Mon–Fri 07:00 — runs before market opens",
    rationale:
      "Morning news collection runs before the market opens at 09:30, so AI sentiment and company-news cards are ready for the trading day.",
  },
  news_refresh_weekend: {
    label: "Weekend news ingestion",
    summary: "Sat 09:00",
    rationale:
      "Captures Saturday FT.lk articles (weekend market commentary) so Monday morning users see fresh content.",
  },
  dividend_alerts_check: {
    label: "Dividend alerts evaluation",
    summary: "Daily 08:00",
    rationale:
      "Runs before market open to fire 'ex-dividend coming up' notifications for symbols held in user portfolios — gives users time to react before trading begins.",
  },
  trial_emails: {
    label: "Trial expiry emails",
    summary: "Daily 09:00",
    rationale:
      "Daily check for users whose 14-day Premium trial has ≤3 days left or has expired — sends 'trial-ending' / 'trial-expired' notifications and downgrades to Basic.",
  },
  token_cleanup: {
    label: "Token cleanup",
    summary: "Daily 00:00",
    rationale:
      "Midnight purge of expired email-verification tokens (>24h old) and password-reset tokens (>1h old).",
  },
};



const SystemStatusPage = () => {
  const { isAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [health, setHealth] = useState(null);
  const [settings, setSettings] = useState({ use_mock_data: false });
  const [confirmMockOpen, setConfirmMockOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshingPrices, setRefreshingPrices] = useState(false);
  const [refreshingFund, setRefreshingFund] = useState(false);
  const [savingMock, setSavingMock] = useState(false);
  const [fundStatus, setFundStatus] = useState(null);
  const [resettingFund, setResettingFund] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      apiGet("/scrape-logs?limit=30"),
      apiGet("/health"),
      apiGet("/settings"),
    ])
      .then(([lg, h, s]) => {
        setLogs(lg);
        setHealth(h);
        setSettings(s);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    // Always refresh fundamentals job status (fast, cheap)
    apiGet("/admin/fundamentals-refresh-status").then(setFundStatus).catch(() => setFundStatus(null));
  };

  useEffect(load, []);

  // Poll fundamentals status every 8s while a job is running so the
  // "Reset stuck job" CTA appears as soon as the lock is stale.
  useEffect(() => {
    if (fundStatus?.status !== "running") return;
    const id = setInterval(() => {
      apiGet("/admin/fundamentals-refresh-status").then(setFundStatus).catch(() => {});
    }, 8000);
    return () => clearInterval(id);
  }, [fundStatus?.status]);

  // Compute "stale lock" — running for >10 min
  const lockMinutesElapsed = (() => {
    if (fundStatus?.status !== "running" || !fundStatus?.started_at) return 0;
    try {
      return Math.max(0, Math.round((Date.now() - new Date(fundStatus.started_at).getTime()) / 60000));
    } catch { return 0; }
  })();
  const showResetButton = fundStatus?.status === "running" && lockMinutesElapsed >= 10;

  const resetFundamentalsLock = async () => {
    setResettingFund(true);
    try {
      await apiPost("/admin/fundamentals-refresh-reset");
      toast.success("Stuck job lock cleared", {
        description: "You can now start a fresh fundamentals refresh.",
      });
      apiGet("/admin/fundamentals-refresh-status").then(setFundStatus);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setResettingFund(false); }
  };

  const triggerPriceRefresh = async () => {
    setRefreshingPrices(true);
    try {
      const res = await apiPost("/admin/refresh-prices");
      const r = res?.result || {};
      const sources = r.source_counts || {};
      const summary = Object.entries(sources)
        .filter(([, n]) => n > 0)
        .map(([k, n]) => `${SOURCE_META[k]?.label || k}: ${n}`)
        .join(" · ");
      toast.success(
        `Prices refreshed (${r.succeeded || 0}/${(r.succeeded || 0) + (r.failed || 0)})${summary ? ` — ${summary}` : ""}`
      );
      load();
    } catch (e) {
      toast.error("Price refresh failed");
    } finally {
      setRefreshingPrices(false);
    }
  };

  const triggerFundamentalsRefresh = async () => {
    setRefreshingFund(true);
    try {
      const res = await apiPost("/admin/refresh-fundamentals");
      if (res?.status === "already_running") {
        const mins = res?.lock_expires_in_seconds != null
          ? Math.max(0, Math.round((20 * 60 - res.lock_expires_in_seconds) / 60))
          : null;
        toast.info(
          `Already running (${res?.progress?.processed || 0}/${res?.progress?.total || 0} processed)` +
            (mins != null ? ` — running for ${mins} min` : ""),
          { description: "If stuck, use the Reset button (appears after 10 min)." },
        );
        apiGet("/admin/fundamentals-refresh-status").then(setFundStatus);
      } else {
        toast.success("Fundamentals refresh started in the background.", {
          description:
            "Polling progress every few seconds — yfinance .CM lookups take ~13 minutes for 280 stocks.",
        });
      }
      // Poll status until completion
      let polls = 0;
      const maxPolls = 240; // ~20 min cap
      const poll = async () => {
        polls += 1;
        try {
          const s = await apiGet("/admin/fundamentals-refresh-status");
          setFundStatus(s);
          if (s?.status === "completed") {
            const summary = (s?.message || "").trim();
            toast.success("Fundamentals refresh complete", {
              description: summary || `Processed ${s?.processed}/${s?.total} stocks.`,
              duration: 9000,
            });
            load();
            return;
          }
          if (s?.status === "failed") {
            toast.error("Fundamentals refresh failed", { description: s?.error || "Unknown error" });
            return;
          }
          if (polls < maxPolls) {
            setTimeout(poll, 5000);
          }
        } catch (e) {
          // Silent — keep polling for a while
          if (polls < maxPolls) setTimeout(poll, 5000);
        }
      };
      setTimeout(poll, 4000);
      load();
    } catch (e) {
      toast.error("Fundamentals refresh failed");
    } finally {
      setRefreshingFund(false);
    }
  };

  const toggleMock = async (newVal) => {
    // Enabling mock data is a destructive action — require explicit confirmation.
    if (newVal && !settings.use_mock_data) {
      setConfirmMockOpen(true);
      return;
    }
    await applyMockToggle(newVal);
  };

  const applyMockToggle = async (newVal) => {
    setSavingMock(true);
    try {
      const updated = await apiPost("/settings", { use_mock_data: newVal });
      setSettings(updated);
      toast.success(
        newVal
          ? "Mock data mode ENABLED — next refresh will use mock data"
          : "Mock data mode disabled — using real CSE data"
      );
    } catch (e) {
      toast.error("Could not save setting");
    } finally {
      setSavingMock(false);
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState description={error} />;

  const ds = health?.data_source || {};
  const lastPrices = ds.last_prices;
  const lastFundamentals = ds.last_fundamentals;
  const lastSnapshot = ds.last_snapshot;

  const effectivePrimary = settings.use_mock_data
    ? "mock"
    : lastPrices?.primary_source || "none";
  const meta = SOURCE_META[effectivePrimary] || SOURCE_META.none;

  return (
    <div className="space-y-6" data-testid="system-status-page">
      {/* 1. ---------------- Top KPI strip ---------------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile
          label="Data source"
          value={`${meta.dot} ${meta.label}`}
          icon={Wifi}
          helpText={meta.description}
          testId="status-data-source"
        />
        <KpiTile
          label="Last price refresh"
          value={lastPrices ? formatDateTime(lastPrices.scrape_date) : "—"}
          icon={Calendar}
          helpText={lastPrices?.status || "no runs yet"}
          testId="status-last-prices"
        />
        <KpiTile
          label="Last fundamentals"
          value={lastFundamentals ? formatDateTime(lastFundamentals.scrape_date) : "—"}
          icon={Database}
          helpText={lastFundamentals?.status || "no runs yet"}
          testId="status-last-fundamentals"
        />
        <KpiTile
          label="Snapshot stocks"
          value={lastSnapshot ? formatNumber(lastSnapshot.total_stocks) : "—"}
          icon={Server}
          helpText={
            lastSnapshot
              ? `Saved ${formatDateTime(lastSnapshot.fetched_at)}`
              : "no snapshot yet"
          }
          testId="status-last-snapshot"
        />
      </div>

      {/* ---------------- Source breakdown ---------------- */}
      {lastPrices?.source_counts && Object.keys(lastPrices.source_counts).length > 0 && (
        <Card className="border bg-card" data-testid="source-breakdown-card">
          <CardHeader>
            <CardTitle className="font-display">Last refresh — source breakdown</CardTitle>
            <p className="text-xs text-muted-foreground">
              How each tracked security received its price on the last run
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(lastPrices.source_counts).map(([k, n]) => {
                const m = SOURCE_META[k] || SOURCE_META.none;
                return (
                  <Badge
                    key={k}
                    variant="outline"
                    className={`${m.badgeClass} text-xs gap-1`}
                    data-testid={`source-count-${k}`}
                  >
                    <span>{m.dot}</span>
                    <span className="font-medium">{m.label}</span>
                    <span className="tabular">· {n}</span>
                  </Badge>
                );
              })}
            </div>
            {Array.isArray(lastPrices.errors) && lastPrices.errors.length > 0 && (
              <div className="mt-4 rounded-lg border border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/5 p-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-[hsl(var(--destructive))]">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Errors on last run ({lastPrices.errors.length})
                </div>
                <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                  {lastPrices.errors.slice(0, 8).map((e, i) => (
                    <li key={i} className="font-mono truncate">
                      • {e}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---------------- Controls (mock toggle + buttons) ---------------- */}
      <Card className="border bg-card" data-testid="status-controls-card">
        <CardHeader>
          <CardTitle className="font-display">Controls</CardTitle>
          <p className="text-xs text-muted-foreground">
            Toggle data source mode and trigger manual refreshes
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            className={`rounded-lg border px-4 py-3 space-y-3 ${
              settings.use_mock_data
                ? "border-destructive/50 bg-destructive/10"
                : "border-warning/40 bg-warning/5"
            }`}
            data-testid="mock-data-control"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <AlertTriangle
                    className={`h-4 w-4 ${
                      settings.use_mock_data ? "text-destructive" : "text-warning"
                    }`}
                  />
                  Use mock data
                  <Badge
                    variant="outline"
                    className="text-[9px] uppercase tracking-wider border-warning/50 text-warning ml-1"
                  >
                    Developer tool only
                  </Badge>
                  {settings.use_mock_data && (
                    <Badge
                      variant="outline"
                      className="text-[9px] uppercase tracking-wider border-destructive/60 text-destructive ml-1"
                      data-testid="mock-data-on-badge"
                    >
                      ON · Showing fake data
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground leading-snug max-w-xl">
                  Enabling this replaces live CSE data with test data. <strong className="text-foreground">Never enable in production.</strong> All users will see fake data if this is ON.
                </p>
              </div>
              <Switch
                checked={!!settings.use_mock_data}
                onCheckedChange={toggleMock}
                disabled={savingMock}
                data-testid="use-mock-data-toggle"
                className={
                  settings.use_mock_data
                    ? "data-[state=checked]:bg-destructive"
                    : ""
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              onClick={triggerPriceRefresh}
              disabled={refreshingPrices}
              className="gap-2 justify-start"
              data-testid="refresh-prices-button"
            >
              <RefreshCcw className={`h-4 w-4 ${refreshingPrices ? "animate-spin" : ""}`} />
              Refresh Prices Now
            </Button>
            <Button
              onClick={triggerFundamentalsRefresh}
              disabled={refreshingFund}
              variant="secondary"
              className="gap-2 justify-start"
              data-testid="refresh-fundamentals-button"
            >
              <RefreshCcw className={`h-4 w-4 ${refreshingFund ? "animate-spin" : ""}`} />
              Refresh Fundamentals Now
            </Button>
          </div>

          {/* Fundamentals job status + stuck-job recovery */}
          {fundStatus?.status === "running" && (
            <div
              className="rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-4 py-3 text-xs flex items-start justify-between gap-3"
              data-testid="fundamentals-job-status"
            >
              <div className="space-y-1">
                <div className="font-medium text-foreground">
                  Fundamentals refresh in progress · {fundStatus.processed || 0} / {fundStatus.total || "?"} processed
                </div>
                <div className="text-muted-foreground">
                  Started {fundStatus.started_at ? formatDateTime(fundStatus.started_at) : "—"}
                  {lockMinutesElapsed > 0 && ` · ${lockMinutesElapsed} min ago`}
                  {showResetButton && " · appears stuck"}
                </div>
              </div>
              {showResetButton && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={resetFundamentalsLock}
                  disabled={resettingFund}
                  className="shrink-0 gap-2"
                  data-testid="fundamentals-reset-stuck-button"
                >
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {resettingFund ? "Resetting…" : "Reset stuck job"}
                </Button>
              )}
            </div>
          )}
          {fundStatus?.status === "completed" && fundStatus?.message && (
            <div className="rounded-lg border bg-muted/30 px-4 py-3 text-xs whitespace-pre-line" data-testid="fundamentals-last-result">
              <div className="font-medium text-foreground mb-1">Last fundamentals refresh</div>
              <div className="text-muted-foreground">{fundStatus.message}</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 4. ---------------- CSE Bulk Import (admin) ---------------- */}
      {isAdmin && <CseImportCard />}

      {/* 4a.1. ---------------- REPORT ANALYSIS QUEUE (admin) ---------------- */}
      {isAdmin && <ReportQueueCard />}

      {/* 4a.2. ---------------- DISK USAGE + CLEANUP (admin) ---------------- */}
      {isAdmin && <DiskUsageCard />}

      {/* 4b. ---------------- TradingView data (admin) ---------------- */}
      {isAdmin && <TradingViewCard />}

      {/* 4c. ---------------- LAUNCH READINESS (admin) ---------------- */}
      {isAdmin && <LaunchChecklistCard />}

      {/* 4c.1. ---------------- API COST TRACKING (admin) ---------------- */}
      {isAdmin && <ApiCostStatusCard />}

      {/* 4c.2. ---------------- DEEP ANALYSIS STATUS (admin) ---------------- */}
      {isAdmin && <DeepAnalysisStatusCard />}

      {/* 4d. ---------------- EMAIL CONFIG (admin) ---------------- */}
      {isAdmin && <EmailConfigCard />}

      {/* 4e. ---------------- SOFT LAUNCH INVITES (admin) ---------------- */}
      {isAdmin && <SoftLaunchInviteCard />}

      {/* 5. ---------------- SMTP Status (admin, read-only indicator) ---------------- */}
      {isAdmin && <SmtpStatusCard />}

      {/* 6. ---------------- Run Now (admin manual trigger) ---------------- */}
      {/* Lets the admin fire any of the 5 user-facing scheduled jobs
          immediately without waiting for the cron clock — handy for
          validating data flows after a prod deploy or after restoring
          LLM budget. Each click runs in a backend background task so the
          page stays interactive. See RunNowCard.jsx + the
          /api/admin/scheduler/run-now/{job_id} endpoint. */}
      {isAdmin && <RunNowCard />}

      {/* 7. ---------------- Scheduler jobs ---------------- */}
      <Card className="border bg-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="font-display">Scheduled jobs</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                APScheduler is bound to <code className="font-mono">Asia/Colombo</code>.
                Each cron trigger is created with the same timezone, so the times below
                are exactly what fires on the server.
              </p>
            </div>
            <Badge
              variant="outline"
              className="gap-1.5 border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/8 text-[hsl(var(--primary))]"
              data-testid="scheduler-timezone-pill"
            >
              <Globe className="h-3 w-3" />
              All times Asia/Colombo (UTC+5:30) — Sri Lanka Standard Time
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <TooltipProvider delayDuration={120}>
            <ul className="divide-y divide-border rounded-lg border bg-background overflow-hidden">
              {(health?.scheduler?.jobs || []).map((j) => {
                const meta = JOB_META[j.id] || {};
                // `last_run` is populated by the backend from scrape_logs.
                // See _enrich_scheduler_jobs_with_last_run in server.py.
                // `null` means the job has never fired (fresh install or
                // no logs matching this job's scrape_type yet).
                const last = j.last_run;
                const lastStatus = last?.status || "";
                const isSuccess = lastStatus === "success";
                const isFailure = lastStatus === "failure" || lastStatus === "error";
                const isPartial = lastStatus === "partial";
                const StatusIcon = isSuccess
                  ? CheckCircle2
                  : isFailure
                    ? XCircle
                    : isPartial
                      ? CircleDashed
                      : CircleDashed;
                const statusColour = isSuccess
                  ? "text-emerald-600 dark:text-emerald-500"
                  : isFailure
                    ? "text-destructive"
                    : isPartial
                      ? "text-amber-600 dark:text-amber-500"
                      : "text-muted-foreground";
                return (
                  <li
                    key={j.id}
                    className="px-4 py-3 flex items-start justify-between gap-3"
                    data-testid={`scheduler-job-${j.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium flex items-center gap-1.5 flex-wrap">
                        <span>{meta.label || j.name}</span>
                        {meta.rationale && (
                          <Tooltip>
                            <TooltipTrigger
                              className="inline-flex"
                              data-testid={`scheduler-job-${j.id}-info`}
                            >
                              <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-primary transition-colors" />
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                              {meta.rationale}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground font-mono truncate">
                        {meta.summary || j.trigger}
                      </div>
                      {/* Last run — pulled from scrape_logs via backend. */}
                      <div
                        className="mt-1 text-[11px] flex items-center gap-1.5 flex-wrap"
                        data-testid={`scheduler-job-${j.id}-last-run`}
                      >
                        <StatusIcon className={`h-3 w-3 shrink-0 ${statusColour}`} />
                        <span className="text-muted-foreground">Last run:</span>
                        {last?.at ? (
                          <>
                            <span className={`font-medium tabular ${statusColour}`}>
                              {formatDateTimeSLT(last.at)}
                            </span>
                            {lastStatus && (
                              <span className={`uppercase tracking-wider text-[9.5px] font-semibold ${statusColour}`}>
                                {lastStatus}
                              </span>
                            )}
                            {last.summary && (
                              <span className="text-muted-foreground/90 truncate max-w-[280px]" title={last.summary}>
                                · {last.summary}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="italic text-muted-foreground">Never</span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground text-right shrink-0">
                      <div>Next:</div>
                      <div className="font-medium tabular text-foreground/80" title="Sri Lanka time (Asia/Colombo)">
                        {formatDateTimeSLT(j.next_run)}
                      </div>
                    </div>
                  </li>
                );
              })}
              {(!health?.scheduler?.jobs || health.scheduler.jobs.length === 0) && (
                <li className="px-4 py-3 text-sm text-muted-foreground">No jobs scheduled</li>
              )}
            </ul>
          </TooltipProvider>
          {/* Manual (admin-triggered) reminder — not on the scheduler */}
          <div
            className="mt-3 rounded-md border border-dashed border-primary/40 bg-primary/5 p-3 flex items-start gap-2"
            data-testid="manual-job-reminder"
          >
            <span className="text-base leading-none">📅</span>
            <div className="text-xs space-y-1">
              <div className="font-medium">
                Monthly: Re-analyse annual reports for multi-year data
              </div>
              <div className="text-muted-foreground leading-snug">
                Manual — admin triggered from{" "}
                <Link to="/admin/reports" className="text-primary hover:underline">
                  Report Monitor
                </Link>{" "}
                → "Re-analyse Annual Reports Only". Run after CSE companies file annual results
                (typically late March / late September) to refresh `historical_fundamentals`
                for Layer 2 CAGR scoring.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ---------------- History table ---------------- */}
      <Card className="border bg-card overflow-hidden">
        <CardHeader>
          <CardTitle className="font-display">Refresh history</CardTitle>
          <p className="text-xs text-muted-foreground">
            Last 30 scraper runs, including per-source counts and errors
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="system-logs-table">
              <thead className="bg-card sticky top-0">
                <tr className="border-b">
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">
                    When
                  </th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">
                    Job
                  </th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">
                    Status
                  </th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">
                    Source
                  </th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">
                    Items
                  </th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">
                    Errors
                  </th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-12 text-center text-sm text-muted-foreground"
                    >
                      No history yet.
                    </td>
                  </tr>
                )}
                {logs.map((l, idx) => {
                  const m = SOURCE_META[l.primary_source] || null;
                  return (
                    <tr key={idx} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="px-4 py-3 whitespace-nowrap">
                        {formatDateTime(l.scrape_date)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="secondary" className="capitalize">
                          {(l.scrape_type || "").replace(/_/g, " ")}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={l.status} />
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {m ? (
                          <span>
                            {m.dot} {m.label}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular">
                        {l.securities_collected ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground truncate max-w-[300px]">
                        {l.errors && l.errors.length > 0
                          ? l.errors.slice(0, 1).join(" · ")
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Mock-data confirmation dialog (developer tool gate) */}
      <AlertDialog open={confirmMockOpen} onOpenChange={setConfirmMockOpen}>
        <AlertDialogContent data-testid="mock-data-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Enable mock data?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 pt-1">
              <span className="block">
                This will replace <strong>ALL live market data</strong> with test
                data for <strong>ALL users</strong>.
              </span>
              <span className="block">
                This should only be used for development testing. Are you sure?
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mock-data-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmMockOpen(false);
                applyMockToggle(true);
              }}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              data-testid="mock-data-confirm-accept"
            >
              Yes, enable mock data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    success:
      "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/25",
    partial:
      "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/25",
    failure:
      "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/25",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${
        map[status] || "bg-muted text-muted-foreground border-border"
      }`}
      data-testid={`status-badge-${status || "unknown"}`}
    >
      <CircleDot className="h-3 w-3 mr-1" />
      {status || "unknown"}
    </span>
  );
};

export default SystemStatusPage;
